import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import apiConfig from './components/pages/config/apiConfig';
import { API_ENDPOINTS } from './components/pages/Auth/utils/constants';

interface UserStatus {
  loading: boolean;
  isAuthenticated: boolean;
  accountStatus: string;
  redirectTo: string | null;
}

const ProtectedRoute = () => {
  const [userStatus, setUserStatus] = useState<UserStatus>({
    loading: true,
    isAuthenticated: false,
    accountStatus: '',
    redirectTo: '/'
  });

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      setUserStatus({
        loading: false,
        isAuthenticated: false,
        accountStatus: '',
        redirectTo: '/'
      });
      return;
    }

    try {
      const response = await axios.get(
        `${apiConfig.apiBaseURL}${API_ENDPOINTS.GET_PROFILE}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const userData = response.data;

      if (userData && userData.accountStatus) {
        const accountStatus = userData.accountStatus?.trim() || 'Inactive';

        setUserStatus({
          loading: false,
          isAuthenticated: true,
          accountStatus,
          redirectTo: getRedirectPath(accountStatus)
        });
      } else {
        handleAuthError();
      }
    } catch (error: any) {
      console.error('Error checking user status:', error);
      localStorage.removeItem('authToken');
      handleAuthError();
    }
  };

  const handleAuthError = () => {
    setUserStatus({
      loading: false,
      isAuthenticated: false,
      accountStatus: '',
      redirectTo: '/'
    });
  };

  const getRedirectPath = (accountStatus: string): string | null => {
    // If account is inactive, user needs to subscribe
    return accountStatus === 'Active' ? null : '/subscription';
  };

  // Show loading spinner
  if (userStatus.loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3957A0',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p style={{ color: '#677289', fontSize: '16px' }}>Verifying your account...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Redirects
  if (!userStatus.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (userStatus.redirectTo) {
    return <Navigate to={userStatus.redirectTo} replace />;
  }

  // All checks passed
  return <Outlet />;
};

export default ProtectedRoute;
